import React from 'react';
import { ArrowLeft, Monitor, Database, Shield, Zap, CheckCircle, Star, ArrowRight, Settings, BarChart3, Users, Lock } from 'lucide-react';

interface DesktopSoftwarePageProps {
  onBack: () => void;
}

const DesktopSoftwarePage: React.FC<DesktopSoftwarePageProps> = ({ onBack }) => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const features = [
    {
      icon: Database,
      title: "Integración Completa",
      description: "Conexión directa con bases de datos, sistemas ERP y herramientas empresariales existentes."
    },
    {
      icon: Shield,
      title: "Seguridad Avanzada",
      description: "Protección de datos empresariales con encriptación y controles de acceso granulares."
    },
    {
      icon: Zap,
      title: "Alto Rendimiento",
      description: "Optimizado para manejar grandes volúmenes de datos y operaciones complejas."
    },
    {
      icon: Settings,
      title: "Personalización Total",
      description: "Interfaces y funcionalidades adaptadas específicamente a tus procesos de negocio."
    }
  ];

  const technologies = [
    {
      name: ".NET Framework",
      description: "Desarrollo robusto para Windows con C# y VB.NET",
      logo: "🔷"
    }
  ];

  const projects = [
    {
      title: "Sistema ERP Manufacturero",
      category: "Manufactura",
      description: "Software completo para gestión de producción, inventarios, calidad y recursos humanos.",
      features: ["Control de producción", "Gestión de inventarios", "Módulo de calidad", "Reportes avanzados"],
      image: "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=600"
    },
    {
      title: "Sistema de Gestión Hospitalaria",
      category: "Salud",
      description: "Plataforma integral para administración hospitalaria con módulos especializados.",
      features: ["Expedientes médicos", "Facturación", "Inventario médico", "Programación citas"],
      image: "https://images.pexels.com/photos/4386466/pexels-photo-4386466.jpeg?auto=compress&cs=tinysrgb&w=600"
    },
    {
      title: "Software de Análisis Financiero",
      category: "Finanzas",
      description: "Herramienta especializada para análisis de riesgo y gestión de portafolios de inversión.",
      features: ["Análisis de riesgo", "Reportes regulatorios", "Dashboard ejecutivo", "Integración bancaria"],
      image: "https://images.pexels.com/photos/590022/pexels-photo-590022.jpeg?auto=compress&cs=tinysrgb&w=600"
    }
  ];

  const process = [
    {
      step: "01",
      title: "Análisis de Requerimientos",
      description: "Estudiamos tus procesos actuales y definimos las funcionalidades específicas que necesitas."
    },
    {
      step: "02",
      title: "Arquitectura del Sistema",
      description: "Diseñamos la estructura técnica considerando escalabilidad, seguridad y rendimiento."
    },
    {
      step: "03",
      title: "Desarrollo e Integración",
      description: "Programamos el software con integración completa a tus sistemas existentes."
    },
    {
      step: "04",
      title: "Pruebas y Validación",
      description: "Realizamos pruebas exhaustivas con usuarios reales en ambiente de producción."
    },
    {
      step: "05",
      title: "Implementación y Soporte",
      description: "Desplegamos el sistema y proporcionamos capacitación y soporte continuo."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors duration-200"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Volver a Servicios</span>
            </button>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-1">
                <img
                  src="/src/assets/logoModelos2.png"
                  alt="ModeloSmart Logo"
                  style={{ height: "120px", width: "auto" }}
                />
              </div>{" "}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-6">
                <div className="flex items-center space-x-3">
                  <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center">
                    <Monitor className="h-8 w-8 text-purple-600" />
                  </div>
                  <h1 className="text-5xl font-bold text-gray-900">
                    Software de <span className="text-purple-600">Escritorio</span>
                  </h1>
                </div>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Desarrollamos aplicaciones de escritorio robustas y especializadas que se integran 
                  perfectamente con tu infraestructura existente y optimizan tus procesos críticos.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => scrollToSection('contacto')}
                  className="inline-flex items-center px-8 py-4 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  Solicitar Cotización
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
                <button
                  onClick={() => scrollToSection('proyectos')}
                  className="inline-flex items-center px-8 py-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:border-purple-600 hover:text-purple-600 transition-all duration-200"
                >
                  Ver Proyectos
                </button>
              </div>

              {/* Platform Icons */}
              <div className="flex items-center space-x-6 pt-4">
                <div className="flex items-center space-x-2">
                  <Monitor className="h-8 w-8 text-gray-700" />
                  <span className="text-gray-600 font-medium">Windows</span>
                </div>


              </div>
            </div>

            {/* Hero Image */}
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/574071/pexels-photo-574071.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Desarrollo de software de escritorio"
                className="w-full h-96 object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-purple-600/20 to-transparent rounded-2xl"></div>
              
              {/* Floating elements */}
              <div className="absolute -top-6 -right-6 bg-white p-4 rounded-xl shadow-lg">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium">Sistema activo</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              ¿Por qué elegir nuestro <span className="text-purple-600">software de escritorio</span>?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Creamos soluciones especializadas que se adaptan perfectamente a tus procesos 
              empresariales específicos y crecen con tu organización.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-gray-50 p-8 rounded-2xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                  <feature.icon className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Tecnologías que <span className="text-purple-600">Utilizamos</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Seleccionamos las mejores herramientas y frameworks para crear software 
              de escritorio robusto y de alto rendimiento.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {technologies.map((tech, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-center"
              >
                <div className="text-4xl mb-4">{tech.logo}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{tech.name}</h3>
                <p className="text-gray-600">{tech.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects */}
      <section id="proyectos" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Proyectos <span className="text-purple-600">Destacados</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ejemplos de software de escritorio que hemos desarrollado para optimizar 
              procesos empresariales en diferentes industrias.
            </p>
          </div>

          <div className="space-y-16">
            {projects.map((project, index) => (
              <div
                key={index}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
              >
                <div className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <div>
                    <span className="inline-block px-3 py-1 bg-purple-100 text-purple-600 text-sm font-medium rounded-full mb-4">
                      {project.category}
                    </span>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">{project.title}</h3>
                    <p className="text-lg text-gray-600 mb-6">{project.description}</p>
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-900">Módulos principales:</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {project.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className={`relative ${index % 2 === 1 ? 'lg:col-start-1' : ''}`}>
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-80 object-cover rounded-2xl shadow-xl"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-purple-600/10 to-transparent rounded-2xl"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Nuestro <span className="text-purple-600">Proceso</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Metodología especializada para desarrollar software de escritorio que realmente 
              resuelva tus desafíos empresariales específicos.
            </p>
          </div>

          <div className="relative">
            {/* Process line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-purple-600 to-blue-600 hidden lg:block"></div>
            
            <div className="space-y-12">
              {process.map((step, index) => (
                <div key={index} className="relative flex items-start">
                  <div className="hidden lg:flex w-16 h-16 bg-purple-600 rounded-full items-center justify-center text-white font-bold text-lg mr-8 flex-shrink-0 shadow-lg">
                    {step.step}
                  </div>
                  
                  <div className="bg-white p-8 rounded-2xl flex-1 shadow-lg hover:shadow-xl transition-shadow duration-300">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{step.title}</h3>
                    <p className="text-gray-600 text-lg">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contacto" className="py-20 bg-gradient-to-br from-purple-900 via-purple-800 to-blue-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-8">
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              ¿Necesitas <span className="text-yellow-400">software especializado</span>?
            </h2>
            <p className="text-xl text-purple-100 max-w-3xl mx-auto">
              Desarrollamos la solución perfecta para tus procesos empresariales únicos. 
              Hablemos sobre tu proyecto.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button
                onClick={onBack}
                className="inline-flex items-center px-8 py-4 bg-white text-purple-900 font-bold text-lg rounded-xl hover:bg-gray-100 transition-all duration-200 transform hover:scale-105 shadow-2xl"
              >
                Solicitar Cotización
                <ArrowRight className="ml-3 h-6 w-6" />
              </button>
              <button
                onClick={onBack}
                className="inline-flex items-center px-8 py-4 border-2 border-white/50 text-white font-semibold text-lg rounded-xl hover:bg-white/10 hover:border-white transition-all duration-200"
              >
                Hablar con un Experto
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DesktopSoftwarePage;